import { useMemo, useState } from 'react';
import Sidebar from '../ui/Sidebar';
import { IconAlertTriangle, IconChevronDown, IconMenu } from '../ui/icons';

export default function AppShell({
  user,
  sites,
  selectedSiteId,
  onSelectSite,
  languages,
  selectedLang,
  onSelectLang,
  unsaved,
  onUnsavedBump,
  modules,
  moduleNotifications,
  route,
  onNavigate,
  onLogout,
  simulation,
  onExitSimulation,
  multiSessionWarning,
  mobileHeaderSticky,
  children,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const selectedSite = useMemo(() => {
    return (sites || []).find((s) => Number(s.id) === Number(selectedSiteId)) || null;
  }, [sites, selectedSiteId]);

  const currentModuleLabel = useMemo(() => {
    if (route === '/' || route === '/dashboard') return 'Dashboard';
    if (route === '/account') return 'Paramètres du compte';
    const found = (modules || []).find((m) => String(m.route) === String(route));
    return found?.label || '—';
  }, [modules, route]);

  const enabledLangs = useMemo(() => {
    return Array.isArray(languages) ? languages.filter((l) => l.is_enabled) : [];
  }, [languages]);

  function langFlagEmoji(code) {
    const c = String(code || '').toLowerCase();
    if (c === 'fr') return '🇫🇷';
    if (c === 'en') return '🇬🇧';
    if (c === 'en-us') return '🇺🇸';
    if (c === 'es') return '🇪🇸';
    if (c === 'de') return '🇩🇪';
    if (c === 'it') return '🇮🇹';
    if (c === 'nl') return '🇳🇱';
    if (c === 'pt') return '🇵🇹';
    if (c === 'pt-br') return '🇧🇷';
    return '🏳️';
  }

  function handleNavigate(path, options) {
    if (unsaved?.dirty) {
      onUnsavedBump?.();
      return;
    }
    if (options?.reselect) {
      window.dispatchEvent(new CustomEvent('omniscope:moduleHome', { detail: { route: path } }));
    }
    onNavigate(path);
    setSidebarOpen(false);
  }

  return (
    <div
      className={`Shell ${sidebarOpen ? 'is-sidebar-open' : ''} ${simulation?.active ? 'is-simulating' : ''} ${
        mobileHeaderSticky ? 'is-mobile-header-sticky' : ''
      }`}
    >
      {sidebarOpen ? <div className="DrawerOverlay" role="presentation" onClick={() => setSidebarOpen(false)} /> : null}

      <div className="Topbar">
        <div className="TopbarCard">
          <button
            type="button"
            className="TopbarRouteToggle"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-expanded={sidebarOpen ? 'true' : 'false'}
            aria-label="Afficher l’arborescence"
          >
            <span className="TopbarRouteToggleIcon" aria-hidden="true">
              <IconMenu />
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span className="TopbarTitle OneLine">{currentModuleLabel}</span>
              <span className="TopbarSubtitle OneLine">
                {selectedSite ? selectedSite.name : '—'}
                {enabledLangs.length > 1 ? ` · ${langFlagEmoji(selectedLang)} ${String(selectedLang || '').toUpperCase()}` : ''}
              </span>
            </span>
            <span className="TopbarRouteToggleIcon TopbarRouteToggleIcon--chevron" aria-hidden="true">
              <IconChevronDown />
            </span>
          </button>
        </div>
      </div>

      <Sidebar
        user={user}
        sites={sites}
        selectedSiteId={selectedSiteId}
        onSelectSite={onSelectSite}
        languages={languages}
        selectedLang={selectedLang}
        onSelectLang={onSelectLang}
        route={route}
        modules={modules}
        moduleNotifications={moduleNotifications}
        onNavigate={handleNavigate}
        onLogout={onLogout}
        onClose={() => setSidebarOpen(false)}
      />
      <main className="Main" role="main">
        {multiSessionWarning ? (
          <div className="MultiSessionBanner" role="status" aria-live="polite">
            <div className="MultiSessionIcon" aria-hidden="true">
              <IconAlertTriangle />
            </div>
            <div className="MultiSessionText">
              <div style={{ fontWeight: 650 }}>
                Plusieurs connexions simultanées détectées
              </div>
              <div className="Muted" style={{ fontSize: 13 }}>
                ({Number(multiSessionWarning.activeSessions || 2)} sessions actives)
              </div>
              <div className="Muted" style={{ fontSize: 13 }}>
                Des conflits de sauvegarde peuvent survenir.
              </div>
            </div>
          </div>
        ) : null}
        {simulation?.active ? (
          <div className="SimBanner" role="status" aria-live="polite">
            <div className="SimBannerLeft">
              <span className="Badge">Vue client simulée</span>
              <span className="Muted">Les modules admin sont masqués.</span>
            </div>
            <button type="button" className="Button Button--ghost" onClick={onExitSimulation}>
              Quitter la simulation
            </button>
          </div>
        ) : null}
        {children}
      </main>

      {unsaved?.dirty ? (
        <div className="UnsavedBar" role="status" aria-live="polite">
          <div key={unsaved.bump || 0} className="UnsavedBarInner" style={unsaved.bump ? { animation: 'UnsavedBump 260ms ease both' } : undefined}>
            <div style={{ minWidth: 0 }}>
              <div className="UnsavedTitle">Modifications non sauvegardées</div>
              {unsaved?.label ? <div className="Muted" style={{ fontSize: 13 }}>{unsaved.label}</div> : null}
            </div>
            <div className="Badges">
              <button type="button" className="Button Button--ghost" onClick={() => unsaved?.onDiscard?.()} disabled={!unsaved?.onDiscard}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={async () => {
                  try {
                    await unsaved?.onSave?.();
                  } catch {
                    // no-op (l'erreur est gérée dans le module)
                  }
                }}
                disabled={!unsaved?.onSave}
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
